import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, Stack, TextField, Typography } from "~/common/mui";

import { SimpleAutocomplete } from "~/common/components/Select/SimpleAutocompleteSelect";
import { useCountryOptions } from "~/features/cart/B2CRequestPage/utils";
import { FormSection } from "~/common/components/FormSection";
import { type RequestAQuoteFormData } from "./RequestAQuoteDialog";
import { PHONE_REGEXP } from "~/common/utils/validations";

interface RequestAQuoteFormProps {
  control: Control<RequestAQuoteFormData>;
  errors: FieldErrors<RequestAQuoteFormData>;
  termsUrl: string;
}

export const RequestAQuoteForm = ({ control, errors, termsUrl }: RequestAQuoteFormProps) => {
  const { t } = useTranslation();
  const countryOptions = useCountryOptions();

  return (
    <Stack gap={3}>
      <FormSection title={t("feature.configurator.requestQuote.section.personal")}>
        <Controller
          name="company"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t("feature.configurator.requestQuote.field.company")}
              fullWidth
              error={!!error}
              helperText={error?.message ? t(error.message) : ""}
            />
          )}
        />
        <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.firstName")}
                required
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.lastName")}
                required
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
        </Stack>
        <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.email")}
                required
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.phone")}
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
                inputProps={{
                  onBeforeInput: (e) => {
                    const event = e as unknown as InputEvent;
                    const val = (e.target as HTMLInputElement).value + event.data;
                    if (!PHONE_REGEXP.test(val.trim())) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  },
                }}
                autoComplete={"tel"}
              />
            )}
          />
        </Stack>
      </FormSection>

      <FormSection title={t("feature.configurator.requestQuote.section.address")}>
        <Controller
          name="address.line1"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t("feature.configurator.requestQuote.field.address")}
              required
              fullWidth
              error={!!error}
              helperText={error?.message ? t(error.message) : ""}
            />
          )}
        />
        <Controller
          name="address.line2"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t("feature.configurator.requestQuote.field.addressAdditional")}
              fullWidth
              error={!!error}
              helperText={error?.message ? t(error.message) : ""}
            />
          )}
        />
        <Stack direction={{ phone: "column", "desktop-s": "row" }} gap={2}>
          <Controller
            name="address.postalCode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.zipCode")}
                required
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
          <Controller
            name="address.city"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("feature.configurator.requestQuote.field.city")}
                required
                fullWidth
                error={!!error}
                helperText={error?.message ? t(error.message) : ""}
              />
            )}
          />
        </Stack>
        <Controller
          name="address.country"
          control={control}
          render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
            <SimpleAutocomplete
              label={t("feature.configurator.requestQuote.field.country") + " *"}
              values={countryOptions}
              fullWidth
              error={!!error}
              helperText={error?.message ? t(error.message) : ""}
              onBlur={onBlur}
              value={value}
              onChange={(val) => onChange(val)}
            />
          )}
        />
      </FormSection>

      <Stack gap={2}>
        <Controller
          name="message"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label={t("feature.configurator.requestQuote.field.message")}
              required
              fullWidth
              multiline
              minRows={2}
              error={!!error}
              helperText={error?.message ? t(error.message) : ""}
            />
          )}
        />
        {/* <Controller
          name="acceptTerms"
          control={control}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <CheckboxWithLabel checked={value} onCheck={onChange} error={!!error}>
              <Trans i18nKey="feature.configurator.requestQuote.terms">
                I accept the{" "}
                <Link href={termsUrl} target="_blank">
                  general terms and conditions of use
                </Link>
                .
              </Trans>
            </CheckboxWithLabel>
          )}
        />
        {errors.acceptTerms && (
          <Typography variant="bodySmall" color="error">
            {t("error.terms.required")}
          </Typography>
        )} */}
        {errors.root && <Alert severity="error">{errors.root.message}</Alert>}
      </Stack>
      <Typography color="text.secondary">{t("configuration.request.paragrapgh.text")}</Typography>
    </Stack>
  );
};
